import Phaser from 'phaser';
import { NET } from '../data/balance';
import { EV } from '../game/events';
import { net } from '../net/NetClient';
import { TEXT_COLOR, UI_FONT } from './theme';
import { focusAfterKey } from './focusAfterKey';

export class ChatOverlay {
  static blocking = false;

  private readonly log: Phaser.GameObjects.Text;
  private readonly lines: string[] = [];
  private readonly input: HTMLInputElement | null;
  private readonly dock: HTMLElement | null;
  private open = false;

  constructor(private readonly scene: Phaser.Scene) {
    this.log = scene.add
      .text(18, scene.scale.height - 210, '', {
        fontFamily: UI_FONT,
        fontSize: '12px',
        color: TEXT_COLOR.bright,
        stroke: '#14141c',
        strokeThickness: 3,
        lineSpacing: 3,
        wordWrap: { width: Math.min(560, scene.scale.width - 36), useAdvancedWrap: true },
      })
      .setScrollFactor(0)
      .setDepth(80)
      .setAlpha(0.92);

    this.dock = document.getElementById('chat-dock');
    this.input = document.getElementById('chat-input') as HTMLInputElement | null;
    this.input?.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.submit();
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
        this.close();
      }
      ev.stopPropagation();
    });
    this.input?.addEventListener('keyup', (ev) => ev.stopPropagation());
    this.input?.addEventListener('keypress', (ev) => ev.stopPropagation());

    net.events.on(EV.netChat, this.onLine, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      net.events.off(EV.netChat, this.onLine, this);
      this.close();
    });
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    if (!net.connected) {
        this.push('* 오프라인입니다. 타이틀에서 O 로 로그인하세요');
      return;
    }
    this.open = true;
    ChatOverlay.blocking = true;
    if (this.dock) this.dock.hidden = false;
    if (this.input) this.input.value = '';
    focusAfterKey(this.input);
  }

  close(): void {
    this.open = false;
    ChatOverlay.blocking = false;
    if (this.dock) this.dock.hidden = true;
    this.input?.blur();
    this.scene.game.canvas?.focus();
  }

  get visible(): boolean {
    return this.open;
  }

  private submit(): void {
    const raw = (this.input?.value ?? '').trim().slice(0, NET.chatMax);
    if (this.input) this.input.value = '';
    if (!raw) {
      this.close();
      return;
    }
    const lower = raw.toLowerCase();
    if (lower.startsWith('/nick ')) {
      net.setNick(raw.slice(6).trim());
    } else if (lower.startsWith('/fadd ')) {
      net.friendAdd(raw.slice(6).trim());
    } else if (lower.startsWith('/fok ')) {
      net.friendAccept(raw.slice(5).trim());
    } else if (lower.startsWith('/w ')) {
      const rest = raw.slice(3).trim();
      const sp = rest.indexOf(' ');
      if (sp > 0) net.whisper(rest.slice(0, sp), rest.slice(sp + 1));
    } else if (lower.startsWith('/p ') || lower.startsWith('/party ')) {
      const name = raw.replace(/^\/(p|party)\s+/i, '').trim();
      net.invite(name);
    } else if (lower === '/leave') {
      net.chat('/leave');
    } else {
      net.chat(raw);
    }
    this.close();
  }

  private onLine(p: { from: string; text: string; whisper?: boolean }): void {
    this.push(`${p.whisper ? '(귓) ' : ''}${p.from}: ${p.text}`);
  }

  private push(line: string): void {
    this.lines.push(line);
    while (this.lines.length > 7) this.lines.shift();
    this.log.setText(this.lines.join('\n'));
  }
}
